import { NgFor, NgIf } from '@angular/common';
import { Component, Input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';

// A list of TPNs as a form control. A TPN still in the input when it loses focus is added too,
// so one typed before pressing Save isn't lost.
@Component({
  selector: 'app-tpn-list-control',
  templateUrl: './tpn-list-control.component.html',
  styleUrls: ['./tpn-list-control.component.scss'],
  standalone: true,
  imports: [NgFor, NgIf, ReactiveFormsModule, ButtonModule, InputTextModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: TpnListControlComponent,
      multi: true,
    },
  ],
})
export class TpnListControlComponent implements ControlValueAccessor {
  @Input() inputId = '';

  protected tpns: string[] = [];
  protected readonly newTpn = new FormControl<string>('', { nonNullable: true });
  protected isDuplicate = false;
  protected isDisabled = false;

  onChange: any = (value: string[]) => {};
  onTouch: any = () => {};

  constructor() {
    this.newTpn.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => (this.isDuplicate = false));
  }

  protected add(): void {
    const tpn = this.newTpn.value.trim();

    if (!tpn) {
      return;
    }

    // The API compares TPNs ignoring case, so the list does too
    this.isDuplicate = this.tpns.some((existingTpn) => existingTpn.toLowerCase() === tpn.toLowerCase());

    if (this.isDuplicate) {
      return;
    }

    this.tpns = [...this.tpns, tpn];
    this.newTpn.reset();
    this.onChange(this.tpns);
    this.onTouch();
  }

  protected remove(tpn: string): void {
    this.tpns = this.tpns.filter((existingTpn) => existingTpn !== tpn);
    this.onChange(this.tpns);
    this.onTouch();
  }

  writeValue(value?: string[] | null): void {
    this.tpns = value ?? [];
    this.newTpn.reset('', { emitEvent: false });
    this.isDuplicate = false;
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;

    if (isDisabled) {
      this.newTpn.disable({ emitEvent: false });
    } else {
      this.newTpn.enable({ emitEvent: false });
    }
  }
}
